"use client";

import { sendGAEvent } from "@next/third-parties/google";

// GA4 custom event names. Keep these stable — they are used to build
// reports/conversions in the GA dashboard.
export const GA_EVENTS = {
  BOOKING_PAGE_VIEW: "booking_page_view",
  BOOKING_STARTED: "booking_started",
  PAYMENT_COMPLETED: "payment_completed",
  PURCHASE: "purchase",
  OT_ENQUIRY_SUBMITTED: "ot_enquiry_submitted",
  CONTACT_FORM_SUBMITTED: "contact_form_submitted",
  CALL_CLICKED: "call_clicked",
  BOOK_APPOINTMENT_CLICKED: "book_appointment_clicked",
} as const;

type GAEventParams = Record<string, string | number | boolean | undefined | unknown>;

// Thin wrapper so call sites never touch the dataLayer directly and a missing
// GA id (e.g. local dev) fails silently instead of throwing.
const trackEvent = (name: string, params: GAEventParams = {}) => {
  if (typeof window === "undefined") return;
  if (!process.env.NEXT_PUBLIC_GAID) return;

  const cleaned = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined),
  );

  try {
    sendGAEvent("event", name, cleaned);
  } catch (error) {
    console.error("Failed to send GA event:", name, error);
  }
};

/**
 * Reads GA4's client_id straight from the `_ga` cookie gtag.js sets, so it can
 * be forwarded to the backend and attached to the server-side `payment_completed`
 * Measurement Protocol backup — without this, a server-fired event has no way
 * to tie back to the visitor's real session and reports as a synthetic client.
 * Cookie value looks like "GA1.2.<id_part1>.<id_part2>"; client_id is the last two parts.
 */
export const getGaClientId = (): string | undefined => {
  if (typeof document === "undefined") return undefined;

  const match = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
  if (!match) return undefined;

  const parts = match[1].split(".");
  if (parts.length < 4) return undefined;

  return parts.slice(-2).join(".");
};

/**
 * Fired once when a user lands on the booking page.
 */
export const trackBookingPageView = (params?: { service?: string }) => {
  trackEvent(GA_EVENTS.BOOKING_PAGE_VIEW, {
    service: params?.service,
  });
};

/**
 * Fired once when a user commits to a service and enters the multi-step flow —
 * either by choosing a service or by landing on a ?service= deep link. This is
 * the funnel's true starting point; BOOKING_PAGE_VIEW also counts bounces.
 *
 * `entry` separates deliberate picks from pre-selected deep links, and `flow`
 * separates the OT enquiry path (which never reaches payment) from paid flows,
 * so the completion rate can exclude it.
 */
export const trackBookingStarted = (params: {
  service: string;
  entry: "service_selected" | "url_param";
  flow: "payment" | "ot_enquiry";
}) => {
  trackEvent(GA_EVENTS.BOOKING_STARTED, {
    service: params.service,
    entry: params.entry,
    flow: params.flow,
  });
};

/**
 * Fired when a user reaches the payment-completed / confirmation screen.
 * Fires both the custom `payment_completed` event and standard GA4 `purchase` event
 * so that default GA4 revenue, conversion, and monetization reports populate automatically.
 */
export const trackPaymentCompleted = (params: {
  source: "booking_flow" | "payment_link" | "second_instalment";
  service?: string;
  value?: number;
  transactionId?: string;
}) => {
  const cleanedPayload = {
    source: params.source,
    service: params.service,
    value: params.value,
    currency: params.value !== undefined ? "AUD" : undefined,
    transaction_id: params.transactionId,
  };

  // 1. Maintain custom event for existing reports/conversions
  trackEvent(GA_EVENTS.PAYMENT_COMPLETED, cleanedPayload);

  // 2. Fire official GA4 standard 'purchase' event so GA4's default 'Total revenue'
  // and monetization reports populate automatically
  if (params.value !== undefined) {
    trackEvent(GA_EVENTS.PURCHASE, {
      transaction_id: params.transactionId || `tx_${Date.now()}`,
      value: params.value,
      currency: "AUD",
      source: params.source,
      service: params.service,
      items: params.service
        ? [
            {
              item_name: params.service,
              item_category: params.service,
              price: params.value,
              quantity: 1,
            },
          ]
        : undefined,
    });
  }
};

/**
 * Fired when a user clicks a "Call" CTA (tel:+611300467513 link). `location` identifies
 * which component/placement the click came from, so click-through can be
 * compared across the different call buttons on the site.
 */
export const trackCallClicked = (params: { location: string }) => {
  trackEvent(GA_EVENTS.CALL_CLICKED, {
    location: params.location,
  });
};

/**
 * Fired when a user clicks any "Book an Appointment" CTA, before navigating
 * to /booking. `location` identifies which component/placement the click
 * came from (hero, cta_banner, contact, navbar, footer, ...), so click-through
 * to the booking flow can be compared across every entry point on the site.
 */
export const trackBookAppointmentClicked = (params: {
  location: string;
  service?: string;
}) => {
  trackEvent(GA_EVENTS.BOOK_APPOINTMENT_CLICKED, {
    location: params.location,
    service: params.service,
  });
};

/**
 * Fired when an Occupational Therapy enquiry form is successfully submitted.
 */
export const trackOtEnquirySubmitted = (params?: {
  enquiryId?: string;
  service?: string;
}) => {
  trackEvent(GA_EVENTS.OT_ENQUIRY_SUBMITTED, {
    service: params?.service || "occupational-therapy",
    enquiry_id: params?.enquiryId,
  });
};

/**
 * Fired when a Contact Us form is successfully submitted.
 */
export const trackContactFormSubmitted = (params?: {
  location?: string;
}) => {
  trackEvent(GA_EVENTS.CONTACT_FORM_SUBMITTED, {
    form_type: "contact_us",
    location: params?.location,
  });
};
